import React from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'

import {
  shouldShowGearModePlayer,
  isBroadcastStreaming,
} from '../../../selectors'
import { PlaybackVideoControlsBarComponent } from '../..'
import { ParticipantStrip } from '../../../../ParticipantStrip/components/ParticipantStrip/ParticipantStrip'
import { StageContainer } from '../../stage-container/stage-container'
import { HeadsUpDisplay } from '../../HeadsUpDisplay'
import { useLocalParticipant } from '../../../../webrtc/webRTCRoom'
import { ParticipantsAudio } from '../../../../MediaTracks/components/ParticipantsAudio/ParticipantsAudio'
import { VideoPlaybackAudioTrack } from '../../../../MediaTracks/components/VideoPlaybackAudioTrack/VideoPlaybackAudioTrack'
import { Transcriptions } from '../../../../Stage/floor/Transcriptions/Transcriptions'
import {
  useFloorState,
  useIsTrayInFloor,
} from '../../../../firebase/hooks/useFloor'
import { useLastActiveMediaTrayCard } from '../../../../firebase/hooks/useTray'

import GearMode from './GearMode'
import Overlay from './Overlay'
import Toolbar from './Toolbar'
import Header from './Header'
import SecondaryBar from './SecondaryBar'

interface WebinarRoomContentProps {
  /** Connected properties */
  isProducer: boolean
  isPresenter: boolean
  isGuest: boolean
  isVideoPlaying: boolean
  isInGearMode: boolean
  showGearModePlayer: boolean
  liveData: unknown
  webinarEnded: boolean
  isEndingWebinar: boolean
  isStreaming: boolean
}

const WebinarRoomContent = ({
  isProducer,
  isPresenter,
  isGuest,
  isVideoPlaying,
  isInGearMode,
  showGearModePlayer,
  liveData,
  webinarEnded,
  isEndingWebinar,
  isStreaming,
}: WebinarRoomContentProps) => {
  const isProducing = isProducer && !webinarEnded && !isEndingWebinar

  const localParticipant = useLocalParticipant()
  const floor = useFloorState()
  const isTrayInFloor = useIsTrayInFloor()
  const lastActiveMediaTrayCard = useLastActiveMediaTrayCard()

  const isLocalParticipantLive =
    floor?.layout === 'solo' &&
    floor.participantId === localParticipant?.id &&
    floor.identifier === 'camera'

  return (
    <div className="webinar-content">
      <Header>
        {!isGuest && (
          <div className="top-corner-info-tools">
            <SecondaryBar />
            <Toolbar />
          </div>
        )}
      </Header>

      {isInGearMode ? (
        <div className="webinar-preview">
          {isProducing && (
            <GearMode
              showGearModePlayer={showGearModePlayer}
              isStreaming={isStreaming}
              liveData={liveData}
            />
          )}
        </div>
      ) : (
        <>
          <div
            className={classnames('webinar-preview', {
              'webinar-presenter-preview': isPresenter,
              'webinar-guest-presenter-preview': isGuest,
            })}
            data-live={isLocalParticipantLive}
          >
            {isProducing &&
              isVideoPlaying &&
              lastActiveMediaTrayCard?.type === 'video' &&
              isTrayInFloor && <PlaybackVideoControlsBarComponent />}
            <StageContainer />
          </div>
          <div className="hud-wrapper">
            <HeadsUpDisplay />
          </div>
          <Transcriptions />
          <ParticipantStrip />
          <ParticipantsAudio />
          <VideoPlaybackAudioTrack />
        </>
      )}
      <Overlay />
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any) => ({
  /**
   * Connected properties
   */
  isVideoPlaying: state.webinarPlayback.isVideoPlaying,
  isInGearMode: state.webinar.isGearMode,
  showGearModePlayer: shouldShowGearModePlayer(state),
  liveData: state.webinar.liveData,
  webinarEnded: state.webinar.webinarEnded,
  isEndingWebinar: state.webinar.isEndingWebinar,
  isStreaming: isBroadcastStreaming(state),
})

// eslint-disable-next-line import-x/no-default-export
export default connect(mapStateToProps)(WebinarRoomContent)
